import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Alert } from "react-bootstrap";

const UpdateModal = ({ show, title, fields, initialValues = {}, onSubmit, onClose }) => {
  const [formValues, setFormValues] = useState({});
  const [message, setMessage] = useState({ type: "", text: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize form values whenever modal opens
  useEffect(() => {
    const initial = fields.reduce((acc, field) => {
      acc[field.name] = initialValues[field.name] || "";
      return acc;
    }, {});
    setFormValues(initial);
    setMessage({ type: "", text: "" }); // clear previous message
  }, [fields, initialValues, show]);

  const handleChange = (e) => {
    setFormValues({ ...formValues, [e.target.name]: e.target.value });
    // Clear message when user starts typing
    if (message.text) {
      setMessage({ type: "", text: "" });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage({ type: "", text: "" });
    
    try {
      const result = await onSubmit(formValues);
      
      if (result && result.success) {
        // Show success message
        setMessage({ type: "success", text: result.message });
        
        // Clear form fields
        const cleared = fields.reduce((acc, field) => {
          acc[field.name] = "";
          return acc;
        }, {});
        setFormValues(cleared);

        // Auto-close modal after 1.5s on success
        setTimeout(() => {
          setMessage({ type: "", text: "" });
          onClose();
        }, 1500);
      } else {
        // Show error message
        setMessage({ 
          type: "danger", 
          text: result?.message || "Update failed. Please try again." 
        });
      }
    } catch (err) {
      // Show error message
      setMessage({ 
        type: "danger", 
        text: err.message || "An unexpected error occurred." 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {/* Message banner - green for success, red for error */}
          {message.text && (
            <Alert
              variant={message.type}
              style={{
                textAlign: "center",
                fontWeight: "bold",
                marginBottom: "20px",
              }}
            >
              {message.text}
            </Alert>
          )}

          {/* Form fields */}
          {fields.map((field) => (
            <Form.Group key={field.name} className="mb-3" controlId={field.name}>
              <Form.Label>{field.label}</Form.Label>
              <Form.Control
                type={field.type}
                name={field.name}
                value={formValues[field.name]}
                onChange={handleChange}
                placeholder={field.placeholder || ""}
                required={field.required ?? true}
                disabled={isSubmitting}
              />
            </Form.Group>
          ))}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Updating..." : "Submit"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default UpdateModal;